<script setup lang="ts">
import { Form } from 'vee-validate'
import Modal from '~common/modal.vue'
import BankSelect from '~/components/mobile/common/bank-select.vue'
import BaseDropdown from '~common/base-dropdown.vue'
import BaseInput from '~/components/common/base-input.vue'
import BaseButton from '~/components/common/base-button.vue'
import ConfirmationModal from '~/components/common/account/modal-add-bank-account-confirm.vue'
import { useDepositData } from '~composables/deposit/use-deposit-data'
import { useBankWithdraw } from '~/composables/withdraw/use-bank'
import { useMainBank } from '~/composables/account/use-main-bank'
import { useModal } from '~/composables/common/use-modal'
import { addBankAccountSchema } from '~/validate/forms/withdraw'
import { ModalCollection } from '~/constants'
import { BANKING_FORM_CRITERIA } from '~/validate/constant/banking-form'
import type { DropdownItem } from '~/composables/common/interface'

const { isMobile } = useDevice()
const { fetchWithdrawBanks } = useDepositData()
const { withdrawBanks } = useBankWithdraw()
const { userBanks, fetchUserMainBanks } = useMainBank()
const { openModal } = useModal()

const formData = ref({ bank_code: '', bank_account_name: '', bank_account_no: '' })
const selectedBank = ref({ name: '', image: '' })

const banksOptions = computed(() => [...withdrawBanks.value.map(bank => {
  const isDisabled = userBanks.value.some(({ bank_code }) => bank.bank_code === bank_code)
  return { ...bank, isDisabled, key: isDisabled ? 'dropdown-item--disabled' : undefined }
})].sort((_, b) => b.isDisabled ? -1 : 1))

const banksOptionsForDropdown = computed(() => [{ value: '', label: 'Chọn ngân hàng' }, ...banksOptions.value])

const selectBank = (item: DropdownItem) => {
  if (typeof item.value !== 'string' || !item.label || !item.image) {
    return
  }
  formData.value.bank_code = item.value
  selectedBank.value = { name: item.label, image: item.image }
}

onMounted(() => {
  fetchWithdrawBanks()
  fetchUserMainBanks()
})

watch(
  () => userBanks.value[0]?.bank_account_name,
  bankAccountName => formData.value.bank_account_name = bankAccountName
)
</script>

<template>
  <div>
    <Modal
      :modalId="ModalCollection.ADD_BANK_ACCOUNT"
      modalDialogClasses="modal-auth__dialog width-480 add-bank-account-dialog"
      modalClass="wrapper-modal-custom"
    >
      <div :class="['modal-add-bank-account', { mobile: isMobile }]">
        <NuxtImg src="/assets/images/icons/add-bank-card.webp" width="88" alt="Add bank card" densities="1x 2x" />

        <div class="text-center">
          <div class="modal-add-bank-account__title">Thêm tài khoản chính chủ</div>
          <div class="modal-add-bank-account__description">
            Nhập thông tin tài khoản chính chủ để đảm bảo an toàn khi giao dịch
          </div>
        </div>

        <Form
          v-slot="{ meta: { valid } }"
          class="w-100"
          :validationSchema="addBankAccountSchema"
          @submit="openModal(ModalCollection.CONFIRM_ADD_BANK_ACCOUNT)"
        >
          <div class="modal-add-bank-account__bank-dropdown">
            <!-- modalId="ADD_BANK_ACCOUNT_SELECT_BANK" -->
            <BankSelect
              v-if="isMobile"
              :modalId="`${ModalCollection.ADD_BANK_ACCOUNT}_${ModalCollection.SELECT_BANK}`"
              :optionItems="banksOptions"
              @handleItemSelected="selectBank"
            />
            <template v-else>
              <div class="modal-add-bank-account__bank-dropdown__label">Ngân hàng</div>
              <BaseDropdown
                :optionItems="banksOptionsForDropdown"
                :valueSelected="formData.bank_code"
                isBankList
                isShowLabel
                isSearchable
                @handleItemSelected="selectBank"
              />
            </template>
          </div>
          
          <BaseInput
            name="bank_account_name"
            label="Họ và tên chủ tài khoản"
            isUppercase
            shouldTrim
            :inputValue="userBanks[0]?.bank_account_name"
            :maxLength="BANKING_FORM_CRITERIA.BANK_NAME_MAX_LENGTH"
            :disabled="!!userBanks[0]?.bank_account_name"
            @onChangeInput="value => formData.bank_account_name = value"
          />

          <BaseInput
            name="bank_account_no"
            label="Số tài khoản"
            class="mb-4"
            :maxLength="BANKING_FORM_CRITERIA.BANK_ACCOUNT_NO_MAX_LENGTH"
            @onChangeInput="value => formData.bank_account_no = value"
          />

          <BaseButton type="submit" :disable="!formData.bank_code || !valid">
            Xác nhận
          </BaseButton>
        </Form>
      </div>
    </Modal>

    <ConfirmationModal :data="formData" :bank="selectedBank" />
  </div>
</template>

<style scoped lang="scss">
.modal-add-bank-account {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  &__title {
    margin-bottom: 4px;
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
    color: $neutral-900;
  }

  &__description {
    font-size: 14px;
    line-height: 24px;
    color: $neutral-600;
  }

  &__bank-dropdown {
    margin-bottom: 16px;

    &__label {
      margin-bottom: 2px;
      font-weight: 600;
      line-height: 24px;
      color: $neutral-700;
    }
  
    :deep(.base-dropdown) {
      .base-dropdown__header {
        width: 100%;
      }

      .dropdown-item--disabled {
        pointer-events: none;

        .dropdown-item__name::after {
          content: 'Đã áp dụng';
          margin-left: 8px;
          padding: 0 4px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          line-height: 16px;
          color: $neutral-900;
          background-color: $neutral-200;
        }

        .dropdown-item__disabled-text {
          display: none;
        }
      }
    }
  }
}
</style>

<style lang="scss">
#ADD_BANK_ACCOUNT_SELECT_BANK {
  .list-select__item--under-maintenance {
    pointer-events: none;
  }

  .dropdown-item--disabled::after {
    content: 'Đã áp dụng'
  }
}
</style>
